import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Settings.css';

function AccountSettings() {
  const { user, updateUser } = useAuth();

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setProfileMsg({ type: 'error', text: 'First name and last name are required.' });
      return;
    }

    setProfileLoading(true);
    setProfileMsg(null);

    try {
      const data = await api.put('/api/auth/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      updateUser(data.user);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({
        type: 'error',
        text: err.response?.data?.error?.message || 'Failed to update profile.',
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!currentPassword) {
      setPasswordMsg({ type: 'error', text: 'Current password is required.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordMsg({ type: 'error', text: 'New password must contain an uppercase letter.' });
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordMsg({ type: 'error', text: 'New password must contain a number.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }

    setPasswordLoading(true);
    setPasswordMsg(null);

    try {
      await api.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
      updateUser({ mustChangePassword: false });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg({
        type: 'error',
        text: err.response?.data?.error?.message || 'Failed to change password.',
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Account Settings</h1>
        <p>Manage your personal information and security preferences</p>
      </div>

      {/* Profile Information */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-icon profile">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="settings-card-header-text">
            <h2>Profile Information</h2>
            <p>Update your name and role</p>
          </div>
        </div>

        {profileMsg && (
          <div className={`settings-alert ${profileMsg.type}`} style={{ marginBottom: 20 }}>
            {profileMsg.type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            <span>{profileMsg.text}</span>
          </div>
        )}

        <form onSubmit={handleProfileSave} className="settings-form">
          {/* Read-only fields */}
          <div className="settings-form-row">
            <div className="settings-field">
              <label>Username</label>
              <div className="settings-readonly">
                <svg className="readonly-lock" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                {user?.username}
              </div>
              <p className="settings-field-hint">Username cannot be changed</p>
            </div>
            <div className="settings-field">
              <label>Email</label>
              <div className="settings-readonly">
                <svg className="readonly-lock" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                {user?.email}
              </div>
              <p className="settings-field-hint">Email cannot be changed</p>
            </div>
          </div>

          {/* Editable fields */}
          <div className="settings-form-row">
            <div className="settings-field">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                className="settings-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
              />
            </div>
            <div className="settings-field">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                className="settings-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="settings-actions">
            <button type="submit" className="settings-btn-primary" disabled={profileLoading}>
              {profileLoading && <span className="settings-spinner" />}
              {profileLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-icon security">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <div className="settings-card-header-text">
            <h2>Change Password</h2>
            <p>Update your password to keep your account secure</p>
          </div>
        </div>

        {passwordMsg && (
          <div className={`settings-alert ${passwordMsg.type}`} style={{ marginBottom: 20 }}>
            {passwordMsg.type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            <span>{passwordMsg.text}</span>
          </div>
        )}

        <form onSubmit={handlePasswordChange} className="settings-form">
          <div className="settings-field">
            <label htmlFor="currentPassword">Current Password</label>
            <div className="settings-password-wrapper">
              <input
                id="currentPassword"
                type={showCurrentPw ? 'text' : 'password'}
                className="settings-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
              <button
                type="button"
                className="settings-password-toggle"
                onClick={() => setShowCurrentPw(!showCurrentPw)}
                tabIndex={-1}
              >
                {showCurrentPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <div className="settings-form-row">
            <div className="settings-field">
              <label htmlFor="newPassword">New Password</label>
              <div className="settings-password-wrapper">
                <input
                  id="newPassword"
                  type={showNewPw ? 'text' : 'password'}
                  className="settings-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  className="settings-password-toggle"
                  onClick={() => setShowNewPw(!showNewPw)}
                  tabIndex={-1}
                >
                  {showNewPw ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <p className="settings-field-hint">Must include uppercase letter and number</p>
            </div>
            <div className="settings-field">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="settings-password-wrapper">
                <input
                  id="confirmPassword"
                  type={showConfirmPw ? 'text' : 'password'}
                  className="settings-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                />
                <button
                  type="button"
                  className="settings-password-toggle"
                  onClick={() => setShowConfirmPw(!showConfirmPw)}
                  tabIndex={-1}
                >
                  {showConfirmPw ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
          </div>

          <div className="settings-actions">
            <button type="submit" className="settings-btn-primary" disabled={passwordLoading}>
              {passwordLoading && <span className="settings-spinner" />}
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AccountSettings;
